import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { createContract, createEscrowHold, getClientPaymentMethods } from '../../../services/contractService';
import toast from 'react-hot-toast';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="flex items-baseline justify-between mb-1.5">
      <span className="text-sm font-semibold text-foreground">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </label>
    {children}
  </div>
);

const inputClass =
  'w-full px-3 py-2 text-sm bg-background border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring transition-shadow';
const textareaClass = inputClass + ' resize-none';

const ContractModal = ({ expert, cases = [], clientId, isOpen, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('form');          // 'form' | 'pay'
  const [contractType, setContractType] = useState('hourly');
  const [caseId, setCaseId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deliverables, setDeliverables] = useState('');
  const [budgetCap, setBudgetCap] = useState('');
  const [totalValue, setTotalValue] = useState('');
  const [deadline, setDeadline] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Payment step state
  const [payMethods, setPayMethods] = useState([]);
  const [selectedMethodId, setSelectedMethodId] = useState('');
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [loadingMethods, setLoadingMethods] = useState(false);

  useEffect(() => {
    if (expert) setTitle(`Expert Review — ${expert.title || expert.full_name || ''}`);
  }, [expert]);

  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setContractType('hourly');
      setCaseId('');
      setDescription('');
      setDeliverables('');
      setBudgetCap('');
      setTotalValue('');
      setDeadline('');
      setPayMethods([]);
      setSelectedMethodId('');
      setStripeCustomerId('');
    }
  }, [isOpen]);

  // Load payment methods when entering payment step
  useEffect(() => {
    if (step !== 'pay' || !clientId) return;
    (async () => {
      setLoadingMethods(true);
      const result = await getClientPaymentMethods(clientId);
      if (result.success) {
        setPayMethods(result.methods);
        setStripeCustomerId(result.stripeCustomerId || '');
        const def = result.methods.find((m) => m.is_default) || result.methods[0];
        if (def) setSelectedMethodId(def.id);
      }
      setLoadingMethods(false);
    })();
  }, [step, clientId]);

  if (!isOpen || !expert) return null;

  const validate = () => {
    if (!title.trim()) { toast.error('Please enter a contract title'); return false; }
    if (!deadline)     { toast.error('Please select a deadline'); return false; }
    if (contractType === 'hourly'    && !budgetCap)  { toast.error('Please enter a budget cap'); return false; }
    if (contractType === 'milestone' && !totalValue) { toast.error('Please enter a total contract value'); return false; }
    // Validate amount is a positive number >= $1
    const parsedAmount = contractType === 'hourly' ? parseFloat(budgetCap) : parseFloat(totalValue);
    if (!parsedAmount || parsedAmount < 1) { toast.error('Contract value must be at least $1.00'); return false; }
    return true;
  };

  const handleReviewPay = (e) => {
    e?.preventDefault();
    if (validate()) setStep('pay');
  };

  const amount = contractType === 'hourly'
    ? parseFloat(budgetCap) || 0
    : parseFloat(totalValue) || 0;

  const handleSubmit = async () => {
    if (!selectedMethodId) { toast.error('Please select a payment method'); return; }
    if (!stripeCustomerId)  { toast.error('No Stripe customer ID found. Please contact support.'); return; }
    try {
      setSubmitting(true);
      const today = new Date().toISOString().split('T')[0];
      // 1. Create the contract record
      const result = await createContract({
        clientId,
        expertId: expert.id,
        caseId: caseId || null,
        contractType,
        title: title.trim(),
        description: description.trim(),
        deliverables: deliverables.trim(),
        hourlyRate: expert.hourly_rate || null,
        budgetCap: contractType === 'hourly' ? amount : null,
        totalContractValue: amount,
        startDate: today,
        deadline,
      });
      if (!result?.success) throw new Error(result?.error || 'Failed to create contract');

      // 2. Create the PaymentIntent and get the clientSecret
      const escrow = await createEscrowHold(result.contract.id, amount, stripeCustomerId, selectedMethodId);
      if (!escrow?.success) throw new Error(escrow?.error || 'Payment authorization failed');

      // 3. Confirm the payment in the browser (handles 3DS / SCA automatically)
      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe.js failed to load');
      const { error: stripeError } = await stripe.confirmCardPayment(escrow.clientSecret, {
        payment_method: selectedMethodId,
      });
      if (stripeError) throw new Error(stripeError.message || 'Payment confirmation failed');

      toast.success('Offer sent & funds held in escrow — the expert will be notified.');
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Unexpected error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Sheet */}
      <div className="relative z-10 bg-card w-full sm:max-w-lg sm:mx-4 max-h-[95vh] sm:max-h-[90vh] rounded-t-3xl sm:rounded-2xl border border-border shadow-2xl flex flex-col overflow-hidden">

        {/* Mobile handle */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            {step === 'pay' && (
              <button
                onClick={() => setStep('form')}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                aria-label="Back"
              >
                <Icon name="ArrowLeft" size={17} />
              </button>
            )}
            <div>
              <h2 className="text-base font-bold text-foreground">
                {step === 'form' ? 'Send Contract Offer' : 'Review & Fund Escrow'}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {step === 'form' ? 'Propose terms to this expert' : 'Funds are held until you release payment'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" aria-label="Close">
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Expert summary strip */}
        <div className="flex items-center gap-3 px-5 py-3 bg-primary/5 border-b border-border flex-shrink-0">
          {expert.avatar_url ? (
            <Image src={expert.avatar_url} alt={expert.full_name} className="w-10 h-10 rounded-xl object-cover ring-2 ring-border" />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="User" size={18} className="text-primary" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-foreground truncate">{expert.full_name}</p>
            <p className="text-xs text-muted-foreground truncate">{expert.title}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-sm font-bold text-foreground">${expert.hourly_rate ?? '—'}/hr</p>
            {expert.average_rating && (
              <p className="text-xs text-muted-foreground">★ {expert.average_rating.toFixed(1)}</p>
            )}
          </div>
        </div>

        {/* ── STEP 1: Contract form ── */}
        {step === 'form' && (
          <form onSubmit={handleReviewPay} className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
            {/* Case */}
            <Field label="Link to a Case" hint="Optional">
              <select value={caseId} onChange={(e) => setCaseId(e.target.value)} className={inputClass}>
                <option value="">— No specific case —</option>
                {cases.map((c) => (
                  <option key={c.id} value={c.id}>
                    #{c.case_number} · {c.property_address || c.allegation_type || 'Case'}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Contract Type" required>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'hourly',    icon: 'Clock', label: 'Hourly Rate', sub: `$${expert.hourly_rate ?? '—'}/hr` },
                  { value: 'milestone', icon: 'Tag',   label: 'Fixed Price', sub: 'Set total upfront' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setContractType(opt.value)}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                      contractType === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40 bg-background'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${contractType === opt.value ? 'bg-primary' : 'bg-muted'}`}>
                      <Icon name={opt.icon} size={16} color={contractType === opt.value ? '#fff' : undefined} className={contractType === opt.value ? '' : 'text-muted-foreground'} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-foreground">{opt.label}</p>
                      <p className="text-[11px] text-muted-foreground">{opt.sub}</p>
                    </div>
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Contract Title" required>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} required />
            </Field>

            <Field label="Scope of Work" required hint="What do you need done?">
              <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the work, context about your case, investigation goals…" className={textareaClass} />
            </Field>

            <Field label="Expected Deliverables" hint="Optional">
              <textarea rows={2} value={deliverables} onChange={(e) => setDeliverables(e.target.value)} placeholder="e.g. Written expert opinion, site inspection report, expert testimony…" className={textareaClass} />
            </Field>

            {contractType === 'hourly' ? (
              <Field label="Budget Cap ($)" required hint={expert.hourly_rate ? `@${expert.hourly_rate}/hr` : undefined}>
                <input type="number" min="1" step="0.01" placeholder="e.g. 1500" value={budgetCap} onChange={(e) => setBudgetCap(e.target.value)} className={inputClass} />
                <p className="text-xs text-muted-foreground mt-1">Work stops when this cap is reached</p>
              </Field>
            ) : (
              <Field label="Total Contract Value ($)" required>
                <input type="number" min="1" step="0.01" placeholder="e.g. 2500" value={totalValue} onChange={(e) => setTotalValue(e.target.value)} className={inputClass} />
              </Field>
            )}

            <Field label="Deadline" required>
              <input type="date" min={today} value={deadline} onChange={(e) => setDeadline(e.target.value)} className={inputClass} required />
            </Field>

            {amount > 0 && (
              <div className="flex items-center justify-between p-3.5 bg-muted/50 rounded-xl border border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="DollarSign" size={15} className="text-primary" />
                  {contractType === 'hourly' ? 'Budget cap (held in escrow)' : 'Fixed price (held in escrow)'}
                </div>
                <span className="text-sm font-bold text-foreground">${amount.toLocaleString()}</span>
              </div>
            )}

            <div className="flex gap-3 p-3.5 bg-amber-400/8 border border-amber-400/30 rounded-xl">
              <Icon name="AlertTriangle" size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                <span className="font-bold text-foreground">Escrow protected: </span>
                Your card is charged when you send the offer. Funds are held securely in the platform — the expert receives payment only after you explicitly release it.
              </p>
            </div>

            {/* Hidden submit for Enter key */}
            <button type="submit" className="sr-only" />
          </form>
        )}

        {/* ── STEP 2: Review & Pay ── */}
        {step === 'pay' && (
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
            {/* Contract summary */}
            <div className="bg-background border border-border rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Contract Summary</p>
              <p className="text-sm font-semibold text-foreground">{title}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1">
                <span className="flex items-center gap-1">
                  <Icon name={contractType === 'hourly' ? 'Clock' : 'Tag'} size={11} />
                  {contractType === 'hourly' ? 'Hourly' : 'Fixed Price'}
                </span>
                {deadline && (
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" size={11} />
                    Due {new Date(deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
                <span className="flex items-center gap-1 font-semibold text-foreground">
                  <Icon name="DollarSign" size={11} />
                  ${amount.toLocaleString()} {contractType === 'hourly' ? 'cap' : 'fixed'}
                </span>
              </div>
            </div>

            {/* Escrow explanation */}
            <div className="flex gap-3 p-4 bg-primary/5 border border-primary/20 rounded-xl">
              <Icon name="ShieldCheck" size={18} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-xs font-semibold text-foreground">How escrow works</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Your card is <span className="font-semibold text-foreground">charged ${amount.toLocaleString()} now</span> and
                  the funds are held in a secure platform escrow account.
                  The expert <span className="font-semibold text-foreground">cannot receive any money</span> until
                  you click "Release Payment" after approving their work.
                  If you cancel, a full refund is issued to your original payment method.
                </p>
              </div>
            </div>

            {/* Payment methods */}
            <div>
              <p className="text-sm font-semibold text-foreground mb-2">Pay with</p>
              {loadingMethods ? (
                <div className="flex items-center justify-center py-8">
                  <Icon name="Loader" size={20} className="text-primary animate-spin" />
                </div>
              ) : payMethods.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                    <Icon name="CreditCard" size={22} className="text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">No saved payment methods</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Add a card in your account settings first.</p>
                  </div>
                  <button
                    onClick={() => { onClose(); navigate('/account?tab=billing'); }}
                    className="text-xs text-primary hover:underline font-medium"
                  >
                    Go to Account Settings →
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {payMethods.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setSelectedMethodId(m.id)}
                      className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                        selectedMethodId === m.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/40 bg-background'
                      }`}
                    >
                      <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <Icon name="CreditCard" size={17} className="text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground capitalize">
                          {m.brand} •••• {m.last4}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Expires {m.exp_month}/{m.exp_year}
                          {m.is_default && <span className="ml-2 text-primary font-medium">Default</span>}
                        </p>
                      </div>
                      {selectedMethodId === m.id && (
                        <Icon name="CheckCircle" size={17} className="text-primary flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Footer ── */}
        <div className="flex items-center gap-3 px-5 py-4 border-t border-border bg-muted/20 flex-shrink-0">
          <Button variant="outline" className="sm:w-28" onClick={step === 'pay' ? () => setStep('form') : onClose} disabled={submitting}>
            {step === 'pay' ? 'Back' : 'Cancel'}
          </Button>
          {step === 'form' ? (
            <Button variant="default" className="flex-1" iconName="ArrowRight" iconPosition="right" onClick={handleReviewPay}>
              Review &amp; Pay
            </Button>
          ) : (
            <Button
              variant="default"
              className="flex-1"
              disabled={submitting || loadingMethods || payMethods.length === 0 || !selectedMethodId}
              iconName={submitting ? 'Loader' : 'ShieldCheck'}
              iconPosition="left"
              onClick={handleSubmit}
            >
              {submitting ? 'Authorizing…' : `Fund Escrow — $${amount.toLocaleString()}`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractModal;
